'use client';

import Link from 'next/link';
import { useState } from 'react';
import { Poppins } from 'next/font/google';
import { BRAND_BLUE, BRAND_GREEN, BRAND_YELLOW } from '@voley/shared-types/constants';

import {
  basicFeatures,
  calculateTotalCredits,
  addOns,
  faqsLeft,
  faqsRight,
  testimonials,
} from './pricing-data';
import type { AddOnType } from './pricing-data';
import dynamic from 'next/dynamic';
import { FAQ } from './components/FAQ';

const WaitlistModal = dynamic(() => import('./components/WaitlistModal').then(m => ({ default: m.WaitlistModal })), { ssr: false });
import { CheckIcon } from './components/CheckIcon';
import { AddOnCard } from './components/AddOnCard';
import { TestimonialCard } from './components/TestimonialCard';

const poppins = Poppins({ subsets: ['latin'], weight: ['700'] });

export default function PricingPage() {
  // Define the brand color to ensure consistency
  const primaryColor = BRAND_BLUE;

  // Waitlist modal state
  const [waitlistModalOpen, setWaitlistModalOpen] = useState(false);
  const [selectedAddOn, setSelectedAddOn] = useState<AddOnType>('VOIP Calling');

  const openWaitlistModal = (addOn: AddOnType) => {
    setSelectedAddOn(addOn);
    setWaitlistModalOpen(true);
  };

  // Message credit calculator state
  const [calculatorUsers, setCalculatorUsers] = useState<number>(1);
  const totalCredits = calculateTotalCredits(calculatorUsers);

  return (
    <div className="bg-white">
      {/* Waitlist Modal */}
      <WaitlistModal
        isOpen={waitlistModalOpen}
        onClose={() => setWaitlistModalOpen(false)}
        addOn={selectedAddOn}
      />

      {/* Header */}
      <div>
        <div className="pt-10 sm:pt-12 pb-16 sm:pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1
                className={`text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl ${poppins.className} tracking-tight leading-tight`}
              >
                <span className="block font-medium" style={{ color: '#111827' }}>
                  Simple pricing
                </span>
                <span className="block font-medium" style={{ color: '#111827' }}>
                  for every agency.
                </span>
              </h1>
              <p className="mt-4 sm:mt-6 max-w-3xl mx-auto text-lg sm:text-xl md:text-2xl font-medium text-gray-600 font-system px-2">
                Clear per-seat pricing built for growing insurance teams — no hidden fees, no long-term contracts.
              </p>
              <p className="mt-3 text-lg sm:text-xl md:text-2xl font-light" style={{ color: BRAND_BLUE }}>
                Start with a 30-day free trial
              </p>
              <div className="mt-6 flex items-center justify-center gap-3">
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: BRAND_BLUE }}
                />
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: BRAND_GREEN }}
                />
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: BRAND_YELLOW }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="relative z-10 -mt-6 sm:-mt-8 py-6 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center mb-6" />

          {/* Plan cards */}
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
              {/* Basic Plan card */}
              <div className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden transform transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] hover:scale-[1.02] hover:-translate-y-0.5 hover:shadow-[0_24px_48px_-12px_rgba(15,23,42,0.25)] flex flex-col">
                <div className="pt-8 px-6 pb-6 sm:px-10 sm:pt-10 sm:pb-8 flex flex-col flex-1">
                  <div>
                    <h3 className="text-3xl leading-9 font-semibold text-gray-900">Core</h3>
                    <p className="mt-3 text-lg text-gray-500 min-h-[56px]">
                      Essentials to get your agency started with modern communications.
                    </p>
                    <div className="mt-4">
                      <span className="block text-5xl font-extrabold tracking-tight text-gray-900">$20</span>
                      <span className="mt-1 block text-xl font-medium text-gray-500">/month per user</span>
                    </div>
                  </div>

                  <div className="mt-6">
                    <Link
                      href="/request-demo"
                      className="w-full flex items-center justify-center px-5 py-3 border-0 text-base font-medium rounded-full text-white shadow-sm transition-all hover:transform hover:scale-[1.02] hover:shadow-lg"
                      style={{ backgroundColor: BRAND_GREEN }}
                    >
                      Get started today
                    </Link>
                  </div>

                  <div className="mt-8 border-t border-gray-200 pt-6 flex-1">
                    <p className="mb-2 text-base text-gray-600">
                      Core plan has a base price of $45 that includes 1,000 message credits per month and an admin user.
                      Each additional user is $20/month and adds another 200 credits per month for your agency.
                    </p>
                    <p className="mb-4 text-sm text-gray-500">
                      Send as many messages as you need — if you exceed your monthly credits, additional credits are just 3&#162; each.
                    </p>
                    <h4 className="text-lg font-semibold mb-4">Everything included:</h4>
                    <ul role="list" className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {basicFeatures.map((feature) => (
                        <li key={feature} className="flex items-start">
                          <div className="flex-shrink-0">
                            <CheckIcon color={primaryColor} />
                          </div>
                          <span className="ml-3 text-base text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Enterprise Plan card */}
              <div className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden transform transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] hover:scale-[1.02] hover:-translate-y-0.5 hover:shadow-[0_24px_48px_-12px_rgba(15,23,42,0.25)] flex flex-col">
                <div className="pt-8 px-6 pb-6 sm:px-10 sm:pt-10 sm:pb-8 flex flex-col flex-1">
                  <div>
                    <h3 className="text-3xl leading-9 font-semibold text-gray-900">Enterprise</h3>
                    <p className="mt-3 text-lg text-gray-500 min-h-[56px]">
                      Custom pricing for unique agency needs.
                    </p>
                    <div className="mt-4">
                      <span className="block text-5xl font-extrabold tracking-tight text-gray-900">Custom</span>
                      <span className="mt-1 block text-xl font-medium text-gray-500">Message volume discounts</span>
                    </div>
                  </div>

                  <div className="mt-6">
                    <Link
                      href="/contact"
                      className="w-full flex items-center justify-center px-5 py-3 border border-gray-300 text-base font-medium rounded-full text-gray-900 bg-white shadow-sm transition-all hover:transform hover:scale-[1.02] hover:shadow-lg hover:bg-gray-50"
                    >
                      Contact sales
                    </Link>
                  </div>

                  <div className="mt-8 border-t border-gray-200 pt-6 flex-1">
                    <p className="mb-4 text-base text-gray-600 min-h-[72px]">
                      For very large agencies or agencies with unique needs. Contact us to discuss custom pricing tailored to your situation.
                    </p>
                    <h4 className="text-lg font-semibold mb-4">Everything included:</h4>
                    <ul role="list" className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {basicFeatures.map((feature) => (
                        <li key={feature} className="flex items-start">
                          <div className="flex-shrink-0">
                            <CheckIcon color={primaryColor} />
                          </div>
                          <span className="ml-3 text-base text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Message credit calculator */}
            <div className="mt-8 sm:mt-10 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 sm:gap-8">
              <div className="max-w-xl lg:w-2/3">
                <div>
                  <p className="text-xs font-semibold tracking-wide text-gray-500 uppercase">Total credits per month</p>
                  <p className="mt-1 text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-gray-900">
                    {totalCredits.toLocaleString()}
                  </p>
                  <p className="mt-2 text-xs sm:text-sm font-medium text-gray-600">
                    SMS = 1 credit · MMS = 3 credits
                  </p>
                </div>

                <h2 className="mt-4 text-base sm:text-lg md:text-xl font-semibold text-gray-900">Message credit calculator</h2>
                <p className="mt-2 text-sm sm:text-base text-gray-600">
                  Adjust the number of users on your Core Plan to see how many message credits your agency receives.
                </p>

                <div className="mt-4 sm:mt-6 max-w-sm">
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                    <span className="font-medium text-gray-700">Number of users</span>
                    <span className="font-semibold text-gray-900">
                      {calculatorUsers} {calculatorUsers === 1 ? 'user' : 'users'}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={1}
                    max={20}
                    value={calculatorUsers}
                    onChange={(e) => setCalculatorUsers(Number(e.target.value))}
                    className="w-full accent-brand"
                  />
                </div>
              </div>

              <div className="lg:w-1/3">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Not sure how many credits you need?</h3>
                <p className="mt-2 text-sm sm:text-base text-gray-600">
                  Our team can help you estimate usage, model different user counts, and choose the right plan for your agency.
                </p>
                <div className="mt-4">
                  <Link
                    href="/contact"
                    className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-sm sm:text-base font-medium rounded-full text-white shadow-sm transition-all hover:transform hover:scale-[1.02] hover:shadow-lg w-full sm:w-auto"
                    style={{ backgroundColor: BRAND_BLUE }}
                  >
                    Talk to our team
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add-ons Section */}
      <div className="bg-white py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center mb-10">
            <h2 className="text-base font-semibold tracking-wide uppercase" style={{ color: primaryColor }}>Add-ons</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Power up your Core plan
            </p>
            <p className="mt-4 max-w-2xl mx-auto text-lg text-gray-600">
              Optional packages you can layer on as your agency grows. Turn them on when you&apos;re ready
              for more channels and automation.
            </p>
            <p className="mt-3 text-sm text-gray-500">
              These features are currently in development. Join the waitlist to get notified when they launch.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {addOns.map((addOn) => (
              <AddOnCard
                key={addOn.name}
                addOn={addOn}
                primaryColor={primaryColor}
                onJoinWaitlist={openWaitlistModal}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Client Testimonial */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center mb-12">
            <h2 className="text-base font-semibold tracking-wide uppercase" style={{ color: primaryColor }}>Testimonials</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              What our customers say
            </p>
          </div>

          <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
            {testimonials.map((testimonial) => (
              <TestimonialCard key={testimonial.name} testimonial={testimonial} />
            ))}
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center mb-12">
            <h2 className="text-base font-semibold tracking-wide uppercase" style={{ color: primaryColor }}>FAQ</h2>
                    <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-text-heading sm:text-4xl">
          Frequently asked questions
        </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              Everything you need to know about Voley&apos;s platform and pricing
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row md:space-x-6">
              <div className="md:w-1/2">
                {faqsLeft.map((faq) => (
                  <FAQ key={faq.question} question={faq.question} answer={faq.answer} />
                ))}
              </div>
              <div className="md:w-1/2 mt-6 md:mt-0">
                {faqsRight.map((faq) => (
                  <FAQ key={faq.question} question={faq.question} answer={faq.answer} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-br from-[#16c2d9]/75 via-brand-green/75 to-brand-yellow/75">
        <div className="max-w-7xl mx-auto py-10 sm:py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-medium tracking-tight text-white">
            <span className="block">Ready to streamline your communications?</span>
            <span className="block opacity-90">Start your journey with Voley today.</span>
          </h2>
          <div className="mt-6 sm:mt-8 flex lg:mt-0 lg:flex-shrink-0">
            <div className="inline-flex w-full sm:w-auto">
              <Link
                href="/request-demo"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-full text-gray-900 bg-white hover:bg-slate-50 transition-all hover:transform hover:scale-[1.02] hover:shadow-lg w-full sm:w-auto"
              >
                Request demo
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
